import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Upload, X, FileText } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface LessonTranscriptUploadProps {
  value: string;
  onChange: (value: string) => void;
}

export function LessonTranscriptUpload({ value, onChange }: LessonTranscriptUploadProps) {
  const [isOpen, setIsOpen] = useState(!!value);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    
    // Parse SRT/VTT files to plain text
    let parsedText = text;
    if (file.name.endsWith('.srt') || file.name.endsWith('.vtt')) {
      parsedText = parseSrtVtt(text);
    }
    
    onChange(parsedText);
  };

  const parseSrtVtt = (content: string): string => {
    // Remove timestamps and sequence numbers from SRT/VTT
    const lines = content.split('\n');
    const textLines: string[] = [];
    
    for (const line of lines) {
      const trimmed = line.trim();
      // Skip empty lines, sequence numbers, timestamps, and VTT headers
      if (!trimmed) continue;
      if (/^\d+$/.test(trimmed)) continue; // Sequence numbers
      if (/^\d{2}:\d{2}/.test(trimmed)) continue; // Timestamps
      if (trimmed.startsWith('WEBVTT')) continue; // VTT header
      if (trimmed.startsWith('NOTE')) continue; // VTT comments
      
      textLines.push(trimmed);
    }
    
    return textLines.join(' ').replace(/\s+/g, ' ').trim();
  };

  const handleClear = () => {
    onChange('');
  };

  const characterCount = value?.length || 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger asChild>
          <Button 
            type="button" 
            variant="ghost" 
            className="w-full justify-between p-4 h-auto"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="font-medium">Transcript</span>
              {value && (
                <span className="text-xs text-muted-foreground">
                  ({characterCount.toLocaleString()} characters)
                </span>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {isOpen ? 'Collapse' : 'Expand'}
            </span>
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="p-4 pt-0 space-y-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="transcript-file" className="cursor-pointer">
                <div className="flex items-center gap-2 px-3 py-2 border rounded-md hover:bg-muted transition-colors">
                  <Upload className="h-4 w-4" />
                  <span className="text-sm">Upload TXT, SRT, or VTT</span>
                </div>
              </Label>
              <input
                id="transcript-file"
                type="file"
                accept=".txt,.srt,.vtt"
                onChange={handleFileUpload}
                className="hidden"
              />
              
              {value && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            <Textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Paste transcript content here or upload a file..."
              rows={6}
              className="font-mono text-sm"
            />
            
            <p className="text-xs text-muted-foreground">
              Transcripts are used by AI to generate quiz questions. Supports plain text, SRT, and VTT formats.
            </p>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
